/* eslint-disable react-hooks/exhaustive-deps */

export type WebSocketMessage = {
  type?: string;
  [key: string]: unknown;
};

type VoidCallback = () => void;
type MsgCallback = (msg: WebSocketMessage) => void;

const STATE = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
} as const;

export class WebSocketService {
  private static instances = new Map<string, WebSocketService>();

  static getInstance(url: string): WebSocketService {
    let inst = this.instances.get(url) as WebSocketService | undefined;

    if (!inst) {
      inst = new WebSocketService(url);
      this.instances.set(url, inst as WebSocketService);
    }

    return inst;
  }

  private socket: WebSocket | null = null;
  private url: string;

  private shouldReconnect = true;
  private reconnectDelayMs = 1000;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  private messageQueue: Array<string> = [];

  private messageListeners = new Set<MsgCallback>();
  private openListeners = new Set<VoidCallback>();
  private closeListeners = new Set<VoidCallback>();

  private constructor(url: string) {
    this.url = url;
  }

  private toWire(data: WebSocketMessage, raw = false): string {
    return raw ? String(data) : JSON.stringify(data);
  }

  connect() {
    if (
      this.socket &&
      (this.socket.readyState === STATE.OPEN ||
        this.socket.readyState === STATE.CONNECTING)
    ) {
      return;
    }

    this.shouldReconnect = true;

    try {
      this.socket = new WebSocket(this.url);
    } catch (err) {
      console.error("❌ WebSocket connection error:", err);
      return;
    }

    this.socket.onopen = () => {
      this.reconnectAttempts = 0;

      try {
        for (const item of this.messageQueue) {
          this.socket?.send(item);
        }
      } catch (e) {
        console.error("❌ Failed while flushing message queue:", e);
      } finally {
        this.messageQueue = [];
      }

      this.openListeners.forEach((cb) => cb());
    };

    this.socket.onmessage = (event) => {
      try {
        let parsed: WebSocketMessage;

        if (typeof event.data === "string") {
          try {
            parsed = JSON.parse(event.data) as WebSocketMessage;
          } catch {
            parsed = { type: "raw" } as WebSocketMessage;
          }
        } else {
          parsed = event.data as WebSocketMessage;
        }

        if (parsed?.error === true && parsed?.message === "Token has expired") {
          this.disconnect();
          return;
        }

        this.messageListeners.forEach((cb) => {
          cb(parsed);
        });
      } catch (err) {
        console.error("❌ Error in onmessage handler:", err);
      }
    };

    this.socket.onclose = () => {
      this.socket = null;

      if (!this.shouldReconnect) {
        this.closeListeners.forEach((cb) => cb());
      }

      if (
        this.shouldReconnect &&
        this.reconnectAttempts < this.maxReconnectAttempts
      ) {
        this.reconnectAttempts++;
        setTimeout(() => this.connect(), this.reconnectDelayMs);
      }
    };
  }

  sendMessage(message: WebSocketMessage, raw = false) {
    const wire = this.toWire(message, raw);

    if (this.socket && this.socket.readyState === STATE.OPEN) {
      try {
        this.socket.send(wire);
        return;
      } catch (e) {
        console.error("❌ WebSocket send error, queuing message:", e);
        this.messageQueue.push(wire);
      }
    } else {
      this.messageQueue.push(wire);

      if (!this.socket || this.socket.readyState === STATE.CLOSED) {
        this.connect();
      }
    }
  }

  onMessage(cb: MsgCallback) {
    this.messageListeners.add(cb);
    return () => this.messageListeners.delete(cb);
  }

  onOpen(cb: VoidCallback) {
    this.openListeners.add(cb);
    return () => this.openListeners.delete(cb);
  }

  onClose(cb: VoidCallback) {
    this.closeListeners.add(cb);
    return () => this.closeListeners.delete(cb);
  }

  disconnect() {
    this.shouldReconnect = false;

    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onclose = null;
      this.socket.onerror = null;

      try {
        this.socket.close();
      } catch (err) {
        console.error("❌ Error while disconnecting WebSocket:", err);
      }

      this.socket = null;
    }
  }

  get readyState() {
    return this.socket?.readyState;
  }
}
